/**
 * timetableApi.js
 * ---------------
 * Real REST API client for Education > Lectures > Time Table module.
 * Routes directly to FastAPI backend /api/lectures/timetable adhering to module_implementation_standards.md.
 */
import api from '../../../../api/client'

export const DAYS_OF_WEEK = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
]

const DAY_MAP = {
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
  Sunday: 7,
}

/**
 * Fetch paginated timetable records matching day tabs, search, and filters.
 */
export async function fetchTimetableList(params = {}) {
  const query = {}

  const dayVal = params.dayFilter || params.dayOfWeek
  if (dayVal && dayVal !== 'All') {
    query.dayOfWeek = DAY_MAP[dayVal] || Number(dayVal) || undefined
  }

  if (params.companyFilter && params.companyFilter !== 'All') {
    const compId = Number(params.companyFilter)
    if (!isNaN(compId)) query.instituteId = compId
  }
  if (params.branchFilter && params.branchFilter !== 'All') {
    const brId = Number(params.branchFilter)
    if (!isNaN(brId)) query.branchId = brId
  }
  if (params.courseFilter && params.courseFilter !== 'All') {
    const cId = Number(params.courseFilter)
    if (!isNaN(cId)) query.courseId = cId
  }
  if (params.batchFilter && params.batchFilter !== 'All') {
    const bId = Number(params.batchFilter)
    if (!isNaN(bId)) query.batchId = bId
  }
  if (params.subjectFilter && params.subjectFilter !== 'All') {
    const sId = Number(params.subjectFilter)
    if (!isNaN(sId)) query.subjectId = sId
  }
  if (params.facultyFilter && params.facultyFilter !== 'All') {
    const fId = Number(params.facultyFilter)
    if (!isNaN(fId)) query.facultyId = fId
  }
  if (params.searchText && params.searchText.trim()) {
    query.searchText = params.searchText.trim()
  }

  // OrderBy map: 0: ID desc, 1: Faculty, 2: Day/Time, 3: Course, 4: Subject, 5: Branch, 6: Batch
  const orderMap = {
    faculty: 1,
    day: 2,
    course: 3,
    subject: 4,
    branch: 5,
    batch: 6,
  }
  query.orderBy = typeof params.orderBy === 'number' ? params.orderBy : orderMap[params.orderBy] || 0

  if (params.page) {
    query.page = params.page
  }
  if (params.pageSize) {
    query.pageSize = params.pageSize
  }

  const { data } = await api.get('/lectures/timetable', { params: query })

  // Map each item to retain backward compatibility with list table columns
  const mappedItems = (data.items || []).map((item) => ({
    id: item.id,
    dayOfWeek: item.day_name,
    planTime: item.p_from,
    tillTime: item.p_till,
    faculty: item.faculty_name,
    course: item.course_name,
    subject: item.subject_name,
    branch: item.branch_names,
    batch: item.batch_names,
    classroom: item.classroom_name,
    company: item.institute_name,
    branchType: item.branch_type === 2 ? 'All' : 'Single',
    batchType: item.batch_type === 2 ? 'All' : 'Single',
    school: item.school_name || 'All',
    schoolType: item.school_type === 2 ? 'All' : 'Single',
    createdByName: item.created_by_name,
    updatedByName: item.updated_by_name,
    raw: item,
  }))

  return {
    items: mappedItems,
    total: data.total,
    page: data.page,
    pageSize: data.page_size,
    totalPages: data.total_pages,
  }
}

/**
 * Fetch single timetable entry by ID.
 */
export async function getTimetableById(id) {
  const { data } = await api.get(`/lectures/timetable/${id}`)
  return data
}

/**
 * Create a new weekly timetable entry.
 */
export async function createTimetableEntry(payload) {
  const { data } = await api.post('/lectures/timetable', payload)
  return data
}

/**
 * Update an existing weekly timetable entry.
 */
export async function updateTimetableEntry(id, payload) {
  const { data } = await api.put(`/lectures/timetable/${id}`, payload)
  return data
}

/**
 * Soft delete a timetable entry.
 */
export async function deleteTimetableEntry(id) {
  const { data } = await api.delete(`/lectures/timetable/${id}`)
  return data
}

/**
 * Mass generate daily lecture rows from the weekly timetable over the date range.
 */
export async function generateLecturesFromTimetable(fromDate, tillDate, extra = {}) {
  const payload = {
    from_date: fromDate,
    to_date: tillDate,
    academic_year: extra.academicYear || '2026-27',
    institute_id: extra.instituteId || 0,
    branch_id: extra.branchId || 0,
    course_id: extra.courseId || 0,
    batch_id: extra.batchId || 0,
    subject_id: extra.subjectId || 0,
    faculty_id: extra.facultyId || 0,
  }
  const { data } = await api.post('/lectures/timetable/generate-lectures', payload)
  return data
}

import { getMasterOptionItems } from '../../../admission/students/api/mastersApi'

export const DAYS_OPTIONS = DAYS_OF_WEEK.map((d) => ({ value: d, label: d }))

export const TIMETABLE_SEARCH_FIELDS = [
  { value: 'All', label: 'All Fields' },
  { value: 'Faculty', label: 'Faculty' },
  { value: 'Subject', label: 'Subject' },
  { value: 'Course', label: 'Course' },
  { value: 'Classroom', label: 'Classroom' },
]

/**
 * Fetch dynamic master options from real backend tables.
 */
export async function fetchTimetableMasterOptions() {
  try {
    const [cItems, bItems, sItems, brItems, compItems, scItems, clItems] = await Promise.all([
      getMasterOptionItems('courses'),
      getMasterOptionItems('batches'),
      getMasterOptionItems('subjects'),
      getMasterOptionItems('branches'),
      getMasterOptionItems('institutes'),
      getMasterOptionItems('schools'),
      getMasterOptionItems('classrooms'),
    ])

    let facItems = []
    try {
      const { data: facData } = await api.get('/options/acheads', { params: { group_id: 33, page: 1, page_size: 100 } })
      facItems = facData?.items || []
    } catch (err) {
      console.error('Error loading faculties from /options/acheads:', err)
    }

    return {
      courses: Array.isArray(cItems) ? cItems.map((c) => ({ value: String(c.id), label: c.name })) : [],
      batches: Array.isArray(bItems) ? bItems.map((b) => ({ value: String(b.id), label: b.name })) : [],
      subjects: Array.isArray(sItems) ? sItems.map((s) => ({ value: String(s.id), label: s.name })) : [],
      branches: Array.isArray(brItems) ? brItems.map((b) => ({ value: String(b.id), label: b.name })) : [],
      companies: Array.isArray(compItems) ? compItems.map((c) => ({ value: String(c.id), label: c.name })) : [],
      schools: Array.isArray(scItems) ? scItems.map((s) => ({ value: String(s.id), label: s.name })) : [],
      classrooms: Array.isArray(clItems) ? clItems.map((r) => ({ value: String(r.id), label: r.name })) : [],
      faculties: Array.isArray(facItems) ? facItems.map((f) => ({ value: String(f.id || f.value), label: f.name || f.label })) : [],
    }
  } catch (err) {
    console.error('Error in fetchTimetableMasterOptions:', err)
    return {
      courses: [],
      batches: [],
      subjects: [],
      branches: [],
      companies: [],
      schools: [],
      classrooms: [],
      faculties: [],
    }
  }
}

/**
 * Static baseline options without demo placeholder strings.
 */
export function getTimetableOptions() {
  return {
    days: DAYS_OPTIONS,
    searchFields: TIMETABLE_SEARCH_FIELDS,
    companies: [],
    branches: [],
    courses: [],
    batches: [],
    schools: [],
    subjects: [],
    faculties: [],
    classrooms: [],
  }
}
